<template>
    <main-layout>
        <div class="page-head" slot="header">
            <div>
                <div class="page-kicker">Tenancies</div>
                <h2>Units &amp; leases</h2>
            </div>
            <div class="page-actions">
                <el-button size="small" icon="el-icon-refresh" @click="syncFromBuildium">Sync from Buildium</el-button>
            </div>
        </div>

        <div :class="{'has-panel': !!detail}">
            <div class="list-filters">
                <el-input v-model="params.q" class="list-search" clearable prefix-icon="el-icon-search"
                          placeholder="Address or unit number" aria-label="Search units"/>
                <button type="button" class="chip" :class="{on: params.outstanding === 'TotalBalance' && !params.outstanding_amount}"
                        @click="toggleOwing">Owing rent
                </button>
                <button type="button" class="chip chip-more" :aria-expanded="String(moreFilters)" @click="moreFilters = !moreFilters">
                    {{ moreFilters ? 'Fewer filters' : '+ Balance age, amount' }}
                </button>
                <span class="spacer"></span>
                <el-dropdown trigger="click" @command="onListMore">
                    <el-button size="small">All {{ total }} matching <i class="el-icon-arrow-down el-icon--right"></i></el-button>
                    <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item command="rtb30all">End of tenancy notices for all {{ total }}…</el-dropdown-item>
                    </el-dropdown-menu>
                </el-dropdown>
            </div>
            <div v-if="moreFilters" class="list-filters">
                <el-select size="small" clearable v-model="params.outstanding" placeholder="Balance owing" @change="onSearch">
                    <el-option v-for="b in balanceBuckets" :key="b.value" :value="b.value" :label="b.label"/>
                </el-select>
                <el-input size="small" class="w200" clearable v-model="params.outstanding_amount" placeholder="More than $"
                          aria-label="Balance more than" @change="onSearch"/>
                <el-button size="small" type="text" @click="clearFilters">Clear all filters</el-button>
            </div>

            <section class="list-card" v-loading="loading">
                <el-table ref="table" :data="dataList" class="list-table" @selection-change="onSelectionChange"
                          @row-click="onRowClick" :row-class-name="rowClass">
                    <el-table-column width="44" type="selection"/>
                    <el-table-column label="Unit" min-width="320">
                        <template slot-scope="{row}">
                            <div class="cell-title">{{ row.formatted_address }}</div>
                            <div class="cell-sub">{{ propertyLabel(row) }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column label="Owing" width="140" align="right">
                        <template slot-scope="{row}">
                            <span v-if="balance(row) > 0" class="cell-num owing">{{ money(balance(row)) }}</span>
                            <span v-else class="text-muted">—</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="" width="130">
                        <template slot-scope="{row}">
                            <span v-if="row.IsUnitOccupied === false || row.IsUnitOccupied === 0" class="pill pill-muted">Vacant</span>
                        </template>
                    </el-table-column>
                </el-table>
                <div class="list-footer">
                    <span class="text-muted">{{ total }} unit{{ total === 1 ? '' : 's' }}</span>
                    <el-pagination background layout="prev, pager, next" :total="total" :page-size="pageSize"
                                   :current-page="page" @current-change="onPageChange"/>
                </div>
            </section>

            <bulk-bar :count="selectionIds.length" @clear="$refs.table.clearSelection()">
                <el-button size="small" @click="rtb30(selectionIds)">End of tenancy notices</el-button>
                <el-dropdown trigger="click" @command="cmd => cmd === 'delete' && batchDelete()">
                    <el-button size="small">More <i class="el-icon-arrow-down el-icon--right"></i></el-button>
                    <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item command="delete" class="danger-item">Remove from the Portal…</el-dropdown-item>
                    </el-dropdown-menu>
                </el-dropdown>
            </bulk-bar>
        </div>

        <side-panel v-if="detail" open kicker="Unit" :title="detail.formatted_address"
                    :subtitle="propertyLabel(detail)"
                    :record-key="detail.Id" @close="detail = null">
            <div class="sp-facts">
                <div><span>Owing</span><b :class="{owing: balance(detail) > 0}">{{ balance(detail) > 0 ? money(balance(detail)) : 'Nothing' }}</b></div>
                <div><span>Market rent</span><b>{{ Number(detail.MarketRent) ? money(detail.MarketRent) : '—' }}</b></div>
                <div><span>Bedrooms · bathrooms</span><b>{{ [detail.UnitBedrooms, detail.UnitBathrooms].filter(Boolean).join(' · ').replace(/([A-Z])/g, ' $1').trim() || '—' }}</b></div>
                <div><span>Size</span><b>{{ detail.UnitSize ? detail.UnitSize + ' sq ft' : '—' }}</b></div>
            </div>
            <div v-if="aging(detail).length" class="unit-aging">
                <span v-for="a in aging(detail)" :key="a.label">{{ a.label }} <b>{{ money(a.value) }}</b></span>
            </div>

            <div class="sp-actions">
                <el-button type="primary" @click="rtb30([detail])">End of tenancy notice</el-button>
                <router-link class="el-button el-button--default" :to="'/forms?unit_id=' + detail.Id">All notices</router-link>
            </div>

            <section v-loading="overviewLoading">
                <h3>Leases</h3>
                <p v-if="overview && !overview.leases.length" class="text-muted">No current or upcoming lease.</p>
                <div v-for="lease in (overview ? overview.leases : [])" :key="lease.id" class="unit-lease">
                    <div class="unit-lease-head">
                        <span class="pill" :class="lease.status === 'Active' ? 'pill-ok' : 'pill-info'">{{ lease.status }}</span>
                        <span>{{ date(lease.from) }} – {{ lease.to ? date(lease.to) : 'month to month' }}</span>
                        <span class="spacer"></span>
                        <b v-if="lease.rent">{{ money(lease.rent) }}</b>
                    </div>
                    <div v-if="lease.rent_since" class="cell-sub">Rent since {{ date(lease.rent_since) }}</div>
                    <ul class="unit-tenants">
                        <li v-for="(t, i) in lease.tenants" :key="i">
                            <b>{{ t.name || 'Tenant' }}</b>
                            <span v-if="t.email"> · <a :href="'mailto:' + t.email">{{ t.email }}</a></span>
                            <span v-if="t.phone"> · {{ t.phone }}</span>
                        </li>
                    </ul>
                </div>
            </section>

            <section v-if="overview && overview.forms.length">
                <h3>Recent notices and reports</h3>
                <router-link v-for="f in overview.forms" :key="f.id" class="unit-form"
                             :to="{path: '/forms', query: {type: f.form_type, open: String(f.id)}}">
                    <b>{{ typeLabel(f.form_type) }}</b>
                    <span class="text-muted">{{ date(f.created_at) }}{{ f.user ? ' · ' + f.user : '' }}</span>
                    <span class="spacer"></span>
                    <span class="pill" :class="f.sent_at ? 'pill-info' : 'pill-muted'">{{ f.sent_at ? 'Sent' : (f.status || '') }}</span>
                </router-link>
            </section>
        </side-panel>

        <modal-generate-rtb30 ref="modalRtb30"/>
    </main-layout>
</template>

<script>
import Pagination from "../mixins/Pagination";
import ApiService from "../utils/ApiService";
import ModalGenerateRtb30 from "./components/ModalGenerateRtb30.vue";
import SidePanel from "../components/SidePanel.vue";
import BulkBar from "../components/BulkBar.vue";
import {FORM_TYPE_LABELS, formatMoney, formatDate} from "../sterling/formHelpers";

const AGES = [
    {key: 'Balance0To30Days', label: '0–30 days'},
    {key: 'Balance31To60Days', label: '31–60 days'},
    {key: 'Balance61To90Days', label: '61–90 days'},
    {key: 'BalanceOver90Days', label: 'Over 90 days'},
];

export default {
    name: "RentalUnits",
    components: {ModalGenerateRtb30, SidePanel, BulkBar},
    mixins: [Pagination],
    data() {
        return {
            pageSize: 50,
            params: {q: '', outstanding: '', outstanding_amount: ''},
            moreFilters: false,
            detail: null,
            overview: null,
            overviewLoading: false,
            searchTimer: null,
            balanceBuckets: [{value: 'TotalBalance', label: 'Any balance'}].concat(AGES.map(a => ({value: a.key, label: a.label}))),
        }
    },
    watch: {
        'params.q'() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(this.onSearch, 350);
        },
        // The top-bar search links here with ?q=…
        '$route.query.q'(value) {
            this.params.q = value || '';
        },
    },
    methods: {
        listApi() {
            return '/buildium/rentals/units';
        },
        listParams() {
            return this.params;
        },
        money: formatMoney,
        date: formatDate,
        typeLabel: (t) => FORM_TYPE_LABELS[t] || t,
        // Property names often begin with the property ID ("B5651_402-4885 Valley Dr_…").
        propertyLabel(unit) {
            const p = unit.property;
            if (!p) return '';
            const name = String(p.Name || '').replace(/_/g, ' ');
            return p.custom_id && !name.startsWith(p.custom_id) ? `${p.custom_id} · ${name}` : name;
        },
        balance(unit) {
            return (unit.outstanding_balances || []).reduce((n, b) => n + (Number(b.TotalBalance) || 0), 0);
        },
        aging(unit) {
            const sums = AGES.map(a => ({label: a.label, value: (unit.outstanding_balances || []).reduce((n, b) => n + (Number(b[a.key]) || 0), 0)}));
            return sums.filter(a => a.value > 0);
        },
        toggleOwing() {
            const on = this.params.outstanding === 'TotalBalance' && !this.params.outstanding_amount;
            this.params.outstanding = on ? '' : 'TotalBalance';
            this.params.outstanding_amount = '';
            this.onSearch();
        },
        clearFilters() {
            this.params.q = '';
            this.params.outstanding = '';
            this.params.outstanding_amount = '';
            this.onSearch();
        },
        onRowClick(row, column) {
            if (column && column.type === 'selection') return;
            this.detail = row;
            this.loadOverview(row.Id);
        },
        rowClass({row}) {
            return this.detail && this.detail.Id === row.Id ? 'is-open' : '';
        },
        loadOverview(id) {
            this.overview = null;
            this.overviewLoading = true;
            ApiService.get(`/buildium/rentals/units/${id}/overview`).then(r => {
                if (this.detail && this.detail.Id === id) this.overview = r.data;
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                this.overviewLoading = false;
            });
        },
        rtb30(units) {
            const ids = units.map(u => u.Id);
            this.$refs.modalRtb30.open((options) => this.generateReports(options, {ids}, ids.length));
        },
        onListMore(cmd) {
            if (cmd === 'rtb30all') {
                this.$refs.modalRtb30.open((options) => this.generateReports(options, this.listParams(), this.total));
            }
        },
        generateReports(options, params, n) {
            this.loading = true;
            ApiService.post('/buildium/rentals/units/generatertb30', {...params, options}).then(() => {
                this.$refs.table && this.$refs.table.clearSelection();
                this.$message.success(`Creating ${n} end of tenancy notice(s); they appear under End of tenancy notices`);
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                this.loading = false;
            });
        },
        batchDelete() {
            const ids = this.selectionIds.map(d => d.Id);
            this.$confirm(`Remove ${ids.length} unit(s) from the Portal? They stay in Buildium and come back with the next sync.`,
                'Remove units', {type: 'warning', confirmButtonText: `Remove ${ids.length}`}).then(() => {
                this.loading = true;
                ApiService.delete('/buildium/rentals/units/batch', {data: {ids}}).then(() => {
                    this.detail = null;
                    this.fetchList();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            }).catch(() => {
            });
        },
        syncFromBuildium() {
            const loading = this.$loading({lock: true, text: 'Synchronizing with Buildium…', background: 'rgba(0, 0, 0, 0.5)'});
            ApiService.get('/buildium/rentals/units/sync').then(() => {
                this.$message.success('Synchronized with Buildium');
                this.fetchList();
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                loading.close();
            });
        },
    },
    mounted() {
        if (this.$route.query.q) {
            this.params.q = this.$route.query.q;
        }
        this.fetchList();
    },
    beforeDestroy() {
        clearTimeout(this.searchTimer);
    },
}
</script>

<style scoped>
.owing {
    color: #B42318;
    font-weight: 600;
}

.unit-aging {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    font-size: 13px;
    color: #5B6478;
}

.unit-lease {
    border: 1px solid #E3E6EE;
    border-radius: 10px;
    padding: 10px 12px;
    margin-bottom: 8px;
}

.unit-lease-head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    font-size: 14px;
}

.unit-tenants {
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
    font-size: 13px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow-wrap: anywhere;
}

.unit-form {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    border-top: 1px solid #EEF0F5;
    color: inherit;
    font-size: 14px;
}

.unit-form:hover {
    text-decoration: none;
    background: #F7F8FB;
}
</style>
